import { selectChangeMessageForCheckbox } from '@UI/aside/asideCheckbox/AsideCheckboxSelectors';
import { selectChangeMessageForFoundationalSuggestions } from '@UI/aside/asideFoundationalSuggestions/AsideFoundationalSuggestionsSelectors';
import { selectChangeMessageForGoalRelations } from '@UI/aside/asideGoalRelations/asideGoalRelationsSelectors';
import { selectChangeMessageForPreviousVersionItems } from '@UI/aside/asidePreviousVersionItems/AsidePreviousVersionItemsSelectors';
import { ContentType } from '@generalTypes/apiTypes';
import { selectChangeMessageForSimpleDropdown } from '@newStore/documentUI/changeMessageSelectors/selectChangeMessageForSimpleDropdown';
import { selectLlinkidGoalNode } from '@newStore/documentUI/transformContent/genericSelectors';
import { type BuildingBlockNodeConfig, NodeType, RequiredType } from '@nodeTypeConfig/configTypes';
import { llinkidDescriptionComponent } from '@nodeTypeConfig/helpers/editComponents/descriptionComponent';
import { identifierComponent } from '@nodeTypeConfig/helpers/editComponents/identifierComponent';

const LLINKID_GOAL: BuildingBlockNodeConfig = {
  nodeSelector: selectLlinkidGoalNode,
  information: {
    single: 'Leerplandoel',
    plural: 'Leerplandoelen',
  },

  createDefaults: {
    llinkidGoalType: 'REGULAR',
  },
  edit: [
    llinkidDescriptionComponent,
    {
      ...identifierComponent,
      required: RequiredType.ERROR,
    },
    {
      component: 'previousVersionItems',
      reactComponent: 'AsidePreviousVersionItems',
      label: 'Vervangt (uit vorige versie)',
      options: {
        relationTypes: ['REPLACES'],
        modal: 'llinkidGoalRelationsModal',
        modalTitle: 'Vervangt (uit vorige versie)',
        placeholder: 'Kies een te vervangen doel',
        relation: 'Doel',
        errorMessage: 'Je moet een geldig doel selecteren',
        type: ContentType.LLINKID_GOAL,
      },
      asideChangeMessageSelector: selectChangeMessageForPreviousVersionItems,
    },
    {
      component: 'llinkidGoalType',
      property: 'llinkidGoalType',
      reactComponent: 'AsideSimpleDropDown',
      label: 'Soort leerplandoel',
      required: RequiredType.ERROR,
      options: {
        clearable: false,
        options: [
          {
            key: 'REGULAR',
            label: 'Gewoon',
          },
          {
            key: 'DEEPENING',
            label: 'Verdiepend',
          },
          {
            key: 'EXTRA',
            label: 'Extra',
          },
          {
            key: 'CHOICE',
            label: 'Keuze',
          },
        ],
      },
      asideChangeMessageSelector: selectChangeMessageForSimpleDropdown,
    },
    {
      component: 'attitudinal',
      property: 'attitudinal',
      label: 'Attitude',
      reactComponent: 'AsideCheckbox',
      asideChangeMessageSelector: selectChangeMessageForCheckbox,
    },
    {
      component: 'goalRelations',
      reactComponent: 'AsideGoalRelations',
      label: 'Gerelateerde doelen ander leerplan',
      options: {
        relationTypes: ['RELATION'],
        modal: 'llinkidGoalRelationsModal',
        listColumn: 'description',
        toType: ContentType.LLINKID_GOAL,
      },
      asideChangeMessageSelector: selectChangeMessageForGoalRelations,
    },
    {
      component: 'goalOdetEndTermReferences',
      reactComponent: 'AsideGoalRelations',
      label: 'Realiseert basisgeletterdheid',
      options: {
        relationTypes: ['REFERENCES'],
        toType: ContentType.CURRICULUM_ODET_DEVELOPMENT_GOAL,
        listColumn: 'title',
      },
      asideChangeMessageSelector: selectChangeMessageForGoalRelations,
    },
    {
      component: 'llinkidFoundationalSuggestions',
      reactComponent: 'AsideFoundationalSuggestions',
      label: 'Distributie suggesties',
      options: {
        relationType: 'SUGGESTED_EDUCATIONAL_ACTIVITY',
        listColumn: 'description',
      },
      showInAncestorTypes: [NodeType.LLINKID_FOUNDATIONAL_CURRICULUM],
      asideChangeMessageSelector: selectChangeMessageForFoundationalSuggestions,
    },
    {
      component: 'goalImplements',
      reactComponent: 'AsideGoalRelations',
      label: 'Wordt (geheel of gedeeltelijk) gerealiseerd door',
      options: {
        relationTypes: ['IMPLEMENTS', 'PARTIALLY_IMPLEMENTS'],
        modal: 'llinkidGoalRelationsModal',
        listColumn: 'description',
        revertedRelationDirection: true,
        toType: ContentType.LLINKID_GOAL,
      },
      showInAncestorTypes: [NodeType.LLINKID_FOUNDATIONAL_CURRICULUM],
      asideChangeMessageSelector: selectChangeMessageForGoalRelations,
    },
    {
      component: 'goalImplements',
      reactComponent: 'AsideGoalRelations',
      label: 'Realiseert (geheel of gedeeltelijk)',
      options: {
        relationTypes: ['IMPLEMENTS', 'PARTIALLY_IMPLEMENTS'],
        modal: 'llinkidGoalRelationsModal',
        listColumn: 'description',
        toType: ContentType.LLINKID_GOAL,
      },
      readonly: true,
      showInAncestorTypes: [NodeType.LLINKID_CURRICULUM],
      hideInAncestorTypes: [NodeType.LLINKID_FOUNDATIONAL_CURRICULUM],
      asideChangeMessageSelector: selectChangeMessageForGoalRelations,
    },
  ],
  buildingBlocks: [
    { type: NodeType.LLINKID_GOAL_DEMARCATION },
    { type: NodeType.LLINKID_PEDAGOGICAL_TIP },
    { type: NodeType.LLINKID_EXTRA_GOAL_INFORMATION },
    { type: NodeType.LLINKID_GOAL_EXPLANATION },
    { type: NodeType.LLINKID_GOAL },
    { type: NodeType.LLINKID_INITIAL_SITUATION },
    { type: NodeType.LLINKID_CONCRETIZATION },
  ],
  documentViewIdentifier: '(p)i.i',
  isCollapsible: false,
  onNewNodeDropped: {
    focusOnField: 'description',
  },
};

export default LLINKID_GOAL;
